import React, { useEffect, useState, useCallback } from 'react';
import { useUpdatePropertyMutation } from '../store/docApi';
import { Box, Button, Checkbox, FormControlLabel, Typography, Tabs, Tab } from '@mui/material';
import {
  AutoFixHigh as WizardIcon,
  Help as QuestionsIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import Stepper from './Stepper';
import ColorChart from '../components/charts/ColorChart';
import ProjectTOC from './ProjectTOC';
import Markdown from '../components/widgets/Markdown';
import Questions from '../Note/Questions';
import Notes from '../Note/Notes';
import { PROGRESS_STATE, PROGRESS_KEY } from '../constants/progress';

export default function ProjectWizard({ item, connectionIndex, stepIndex, stepId, pattern, onStepChange }) {
  const [currentSize, setCurrentSize] = useState();
  const [updateProperty] = useUpdatePropertyMutation();
  const [currentStep, setCurrentStep] = useState();
  const [currentStepIndex, setCurrentStepIndex] = useState(stepIndex);
  const [currentStepId, setCurrentStepId] = useState(stepId);
  const [progress, setProgress] = useState();
  const [showMotifView, setShowMotifView] = useState(true);
  const [isTOCOpen, setIsTOCOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Reset activeTab when currentStep changes
  useEffect(() => {
    setActiveTab(0);
  }, [currentStep]);

  useEffect(() => {
    if (item?.connections?.length > connectionIndex) {
      const connection = item.connections[connectionIndex];
      setProgress(connection.progress);
      setCurrentSize(connection.currentSize);
    }
  }, [item, connectionIndex]);

  // Find step by ID with depth
  const findStepByIdWithDepth = useCallback((steps, targetId, depth = '') => {
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const stepId = depth ? `${depth}_${i}` : `${i}`;
      if (stepId === targetId) {
        return { step, index: i };
      }
      if (step.steps) {
        const result = findStepByIdWithDepth(step.steps, targetId, stepId);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }, []);

  useEffect(() => {
    if (pattern?.steps?.length > 0) {
      let _currentStep;
      if (stepId) {
        // If we have a stepId, find the step by ID
        const result = findStepByIdWithDepth(pattern.steps, stepId);
        if (result) {
          _currentStep = { ...result.step, stepIndex: result.index };
          setCurrentStepIndex(result.index);
          setCurrentStepId(stepId);
        }
      } else if (currentStepIndex >= 0 && currentStepIndex < pattern.steps.length) {
        // Otherwise use the stepIndex
        _currentStep = { ...pattern.steps[currentStepIndex], stepIndex: currentStepIndex };
      }

      if (_currentStep) {
        if (_currentStep.part_id && pattern.parts) {
          const part = pattern.parts[_currentStep.part_id];
          if (part) _currentStep = { ..._currentStep, ...part };
        }
        setCurrentStep(_currentStep);
        window.scrollTo(0, 0);
      }
    }
  }, [pattern, stepId, currentStepIndex, findStepByIdWithDepth]);

  const getProgress = (stepId) => progress?.[PROGRESS_KEY.STEP(stepId)] || PROGRESS_STATE.NOT_STARTED;

  const updateProgress = async (stepId, value) => {
    const newProgress = { ...progress, [PROGRESS_KEY.STEP(stepId)]: value };
    setProgress(newProgress);
    await updateProperty({
      label: 'project',
      pk: item.pk,
      id: item.id,
      name: `connections/${connectionIndex}/progress/${PROGRESS_KEY.STEP(stepId)}`,
      value
    });
  };

  const handleNextStep = () => {
    setCurrentStepId(null); // Clear stepId when using next/prev
    const newIndex = currentStepIndex + 1;
    setCurrentStepIndex(newIndex);
    if (onStepChange) {
      onStepChange(newIndex.toString());
    }
    // Mark current step as completed and next step as in progress
    if (currentStepIndex >= 0) {
      updateProgress(currentStepIndex.toString(), PROGRESS_STATE.COMPLETED);
    }
    updateProgress(newIndex.toString(), PROGRESS_STATE.IN_PROGRESS);
  };

  const handlePrevStep = () => {
    setCurrentStepId(null); // Clear stepId when using next/prev
    const newIndex = currentStepIndex - 1;
    setCurrentStepIndex(newIndex);
    if (onStepChange) {
      onStepChange(newIndex.toString());
    }
    // Mark current step as in progress and previous step as in progress
    updateProgress(currentStepIndex.toString(), PROGRESS_STATE.IN_PROGRESS);
    updateProgress(newIndex.toString(), PROGRESS_STATE.IN_PROGRESS);
  };

  const handleStepPress = (stepId) => {
    // Handle both string and number stepIds
    const firstNumber = typeof stepId === 'string' ? stepId.split('_')[0] : stepId.toString();
    const stepIndex = parseInt(firstNumber);

    // Update both values in a single state update
    setCurrentStepId(stepId);
    setCurrentStepIndex(stepIndex);
    if (onStepChange) {
      onStepChange(stepId);
    }
    // Mark the selected step as in progress
    updateProgress(stepId, PROGRESS_STATE.IN_PROGRESS);
  };

  const handleStepperPress = (index) => {
    setCurrentStepId(null); // Clear stepId when using stepper
    setCurrentStepIndex(index);
    // Also select the corresponding top-level item in TOC
    const stepId = `${index}`;
    setCurrentStepId(stepId);
    if (onStepChange) {
      onStepChange(stepId);
    }
    // Mark the selected step as in progress
    updateProgress(stepId, PROGRESS_STATE.IN_PROGRESS);
  };

  const NextPrevButtons = () => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
      {currentStepIndex > 0 && (
        <Button onClick={handlePrevStep} variant="outlined" startIcon="←">
          Back
        </Button>
      )}
      {currentStepIndex < pattern.steps.length - 1 && (
        <Button onClick={handleNextStep} variant="outlined" endIcon="→">
          Next
        </Button>
      )}
    </Box>
  );

  const Steps = ({ steps, depth = '', parent }) =>
    steps?.map((step, i) => {
      const stepId = depth ? `${depth}_${i}` : `${i}`;
      const stepProgress = getProgress(stepId);
      const optionIndex = progress?.[PROGRESS_KEY.OPTION(stepId)] || 0;
      const selectedOption = step.options?.[optionIndex];
      if (step.sizes && !step.sizes.includes(currentSize)) return null;
      const level = depth ? depth.toString().split('_').length : 0;
      return (
        <Box key={i} sx={{ ml: level * 2, mt: 1 }}>
          {step.title && (
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              {step.title}
            </Typography>
          )}
          {step.name && <Typography sx={{ mb: 1 }}>{step.name}</Typography>}
          {step.note && <Markdown text={step.note} />}
          {parent?.multiselect ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={stepProgress === PROGRESS_STATE.COMPLETED}
                  onChange={() => updateProgress(stepId, stepProgress === PROGRESS_STATE.COMPLETED ? PROGRESS_STATE.NOT_STARTED : PROGRESS_STATE.COMPLETED)}
                />
              }
              label={step.verbal}
            />
          ) : (
            <Box
              onClick={() => updateProgress(stepId, stepProgress === PROGRESS_STATE.COMPLETED ? PROGRESS_STATE.NOT_STARTED : PROGRESS_STATE.COMPLETED)}
              sx={{
                p: 1,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: stepProgress === PROGRESS_STATE.COMPLETED ? 'primary.main' : 'grey.400',
                backgroundColor: stepProgress === PROGRESS_STATE.COMPLETED ? 'primary.light' : 'transparent'
              }}
            >
              {step.name && <Typography>{step.name}</Typography>}
              {step.verbal && <Markdown text={step.verbal} />}
            </Box>
          )}
          {step.options && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {step.options.map((option, j) => (
                <Button
                  key={j}
                  onClick={() => {
                    updateProperty({
                      label: 'project',
                      pk: item.pk,
                      id: item.id,
                      name: `connections/${connectionIndex}/progress/${PROGRESS_KEY.OPTION(stepId)}`,
                      value: j
                    });
                  }}
                  variant={optionIndex === j ? 'contained' : 'outlined'}
                >
                  {option.name}
                </Button>
              ))}
            </Box>
          )}
          {selectedOption && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'grey.400' }}>
              {selectedOption.name && <Typography>{selectedOption.name}</Typography>}
              {selectedOption.verbal && <Markdown text={selectedOption.verbal} />}
              {selectedOption.steps && <Steps steps={selectedOption.steps} depth={stepId} parent={selectedOption} />}
            </Box>
          )}
          {step.steps && <Steps steps={step.steps} depth={stepId} parent={step} />}
          {step.colorchart && !showMotifView && (
            <ColorChart key={stepId} item={step} depth={stepId} viewonly progress={progress} updateProgress={updateProgress} />
          )}
          {step.endnote && <Typography>{step.endnote}</Typography>}
          {step.picture && <img src={step.picture} alt={step.name} style={{ width: '100%', maxWidth: 600, marginBottom: 8 }} />}
        </Box>
      );
    });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getTabs = () => {
    const tabs = [];

    // Instructions tab is always available
    tabs.push({
      label: 'Instructions',
      icon: <WizardIcon />,
      component: (
        <Box>
          {currentStep.title && (
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              {currentStep.title}
            </Typography>
          )}
          {currentStep.name && <Typography sx={{ mb: 1 }}>{currentStep.name}</Typography>}
          {currentStep.note && <Markdown text={currentStep.note} />}
          {currentStep.verbal && <Markdown text={currentStep.verbal} />}
          {currentStep.steps && <Steps steps={currentStep.steps} depth={currentStepId || currentStepIndex} parent={currentStep} showVerbal={true} expandAll={true} />}
          {currentStep.colorchart && !showMotifView && (
            <ColorChart
              key={currentStep.stepIndex}
              item={currentStep}
              depth={currentStepId || currentStepIndex}
              viewonly
              progress={progress}
              updateProgress={updateProgress}
            />
          )}
          {currentStep.endnote && <Typography>{currentStep.endnote}</Typography>}
          <NextPrevButtons />
          {currentStep.colorchart && (
            <FormControlLabel
              control={<Checkbox checked={showMotifView} onChange={() => setShowMotifView(!showMotifView)} />}
              label="Show Motif View"
            />
          )}
        </Box>
      )
    });

    if (currentStep?.questions) {
      tabs.push({
        label: 'Questions',
        icon: <QuestionsIcon />,
        component: (
          <Questions
            item={item}
            connectionIndex={connectionIndex}
            currentStep={currentStep}
            stepIndex={currentStepIndex}
          />
        )
      });
    }

    tabs.push({
      label: 'Notes',
      icon: <NoteIcon />,
      component: (
        <Notes
          project={item}
          connectionIndex={connectionIndex}
          currentStepId={stepId}
          currentStep={currentStep}
        />
      )
    });

    return tabs;
  };

  return pattern?.steps && item && currentStep ? (
    <Box>
      {currentSize && <Typography>Size: {currentSize}</Typography>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button variant="text" onClick={() => setIsTOCOpen(!isTOCOpen)}>
          {isTOCOpen ? 'Hide TOC' : 'Show TOC'}
        </Button>
        <Stepper
          steps={pattern.steps}
          currentStep={Number(currentStepIndex)}
          onStepPress={handleStepperPress}
        />
      </Box>
      <Box sx={{ display: 'flex' }}>
        <ProjectTOC
          steps={pattern.steps}
          currentStep={currentStepId || currentStepIndex}
          onStepPress={handleStepPress}
          isOpen={isTOCOpen}
        />
        <Box sx={{ flex: 1 }}>
          {(() => {
            const tabs = getTabs();
            if (tabs.length === 0) return null;

            return (
              <>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {tabs.map((tab, index) => (
                    <Tab
                      key={index}
                      label={tab.label}
                      icon={tab.icon}
                      iconPosition="start"
                    />
                  ))}
                </Tabs>
                <Box sx={{ mt: 2 }}>
                  {tabs[activeTab]?.component}
                </Box>
              </>
            );
          })()}
        </Box>
      </Box>
    </Box>
  ) : null;
}
